/* 三國逗陣打 · 角色骨架（程式繪製）
 * 原點在腳底中心，角色面向 +x。
 * 肢體角度一律用「0 = 朝下、90 = 朝前、180 = 朝上」。
 * 姿勢由 18 個參數組成（見 BASE），動畫就是這些參數的關鍵影格。 */
// @ts-nocheck — 第 2 步只把檔案改成模組，型別在第 4 步補
import { SG } from '../core/sg.ts';

const D = Math.PI / 180;
const TAU = Math.PI * 2;
const INK = '#1b1411';
const OL = 2.2;
SG.INK = INK;

const down = (a) => [Math.sin(a * D), Math.cos(a * D)];
const up = (a) => [Math.sin(a * D), -Math.cos(a * D)];
const add = (p, d, l) => [p[0] + d[0] * l, p[1] + d[1] * l];

const shadeCache = new Map();
function shade(hex, f) {
  const key = hex + f;
  let v = shadeCache.get(key);
  if (v) return v;
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  if (f < 1) { r *= f; g *= f; b *= f; } else { const k = f - 1; r += (255 - r) * k; g += (255 - g) * k; b += (255 - b) * k; }
  v = `rgb(${r | 0},${g | 0},${b | 0})`;
  shadeCache.set(key, v);
  return v;
}
SG.shade = shade;

// ---------- 繪圖小工具 ----------
function line(ctx, a, b) { ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke(); }
// 描邊膠囊：先畫全部墨線再畫全部填色，關節處不會出現接縫
// 立體感：每個色塊再疊一層由上而下的光影（頂部提亮、底部壓暗），在角色的局部座標裡計算，
// 所以小兵縮小、樹木放大都跟著縮放。SG.SHADE = false 可以關掉，回到純平塗。
SG.SHADE = true;
// 全場唯一的太陽：光從左上來，影子往右下（ang 是影子在地面上的方向，len 是影子長度係數）
SG.SUN = { ang: 0.5, len: 0.62 };
// 光影改成斜向：左上亮、右下暗，所以角色朝向的那一側會吃到邊光。角色是左右翻轉畫的，
// 用 FACE（Actor.draw 設定）把漸層換到世界座標的左上
let FACE = 1;
SG.setFace = (f) => { FACE = f < 0 ? -1 : 1; };
const GRADS = new WeakMap();
function shadeGrad(ctx) {
  let g = GRADS.get(ctx);
  if (!g) {
    g = {};
    for (const F of [1, -1]) {
      const lg = ctx.createLinearGradient(-46 * F, -150, 34 * F, 14);
      lg.addColorStop(0, 'rgba(255,246,220,.24)'); lg.addColorStop(0.42, 'rgba(255,246,220,0)'); lg.addColorStop(0.68, 'rgba(20,12,10,0)'); lg.addColorStop(1, 'rgba(20,12,10,.32)');
      g[F] = lg;
    }
    GRADS.set(ctx, g);
  }
  return g[FACE];
}
function caps(ctx, list) {
  ctx.lineCap = 'round';
  ctx.strokeStyle = INK;
  for (const s of list) { ctx.lineWidth = s[2] + OL * 2; line(ctx, s[0], s[1]); }
  for (const s of list) { ctx.strokeStyle = s[3]; ctx.lineWidth = s[2]; line(ctx, s[0], s[1]); }
  if (SG.SHADE) { ctx.strokeStyle = shadeGrad(ctx); for (const s of list) { ctx.lineWidth = s[2]; line(ctx, s[0], s[1]); } }
}
function path(ctx, pts) {
  ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
}
function ink(ctx, fill, lw) {
  if (fill) { ctx.fillStyle = fill; ctx.fill(); if (SG.SHADE && typeof fill === 'string') { ctx.fillStyle = shadeGrad(ctx); ctx.fill(); } }
  ctx.lineJoin = 'round'; ctx.lineWidth = lw || OL; ctx.strokeStyle = INK; ctx.stroke();
}
function poly(ctx, pts, fill, lw) { path(ctx, pts); ink(ctx, fill, lw); }
function dot(ctx, x, y, r, fill, lw) { ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ink(ctx, fill, lw); }
// 斜射光的投影：往右下拉長的柔影（rx、ry 是接地橢圓的半徑，h 是物體高度）
function castShadow(ctx, x, y, rx, ry, h, alpha) {
  if (!SG.SHADE) { ctx.fillStyle = 'rgba(8,6,4,.38)'; ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, TAU); ctx.fill(); return; }
  const a = alpha == null ? 0.26 : alpha, len = h * SG.SUN.len, ang = SG.SUN.ang;
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = `rgba(6,8,4,${a})`;
  ctx.beginPath(); ctx.ellipse(Math.cos(ang) * len * 0.45, Math.sin(ang) * len * 0.3 + ry * 0.4, rx + len * 0.5, ry + len * 0.14, ang * 0.75, 0, TAU); ctx.fill();
  ctx.fillStyle = `rgba(6,8,4,${a * 1.5})`; // 接地的暗影
  ctx.beginPath(); ctx.ellipse(0, 0, rx, ry, 0, 0, TAU); ctx.fill();
  ctx.restore();
}
SG.draw = { caps, poly, dot, ink, path, line, INK, castShadow };

function chain(o, a1, l1, a2, l2) {
  const j = add(o, down(a1), l1);
  return { o, j, e: add(j, down(a2), l2) };
}

// ---------- 兵器（區域座標：+x 沿兵器方向，原點在握把） ----------
function pole(ctx, a, b, w, col) { caps(ctx, [[[a, 0], [b, 0], w, col]]); }
const WEAPONS = {
  guandao(ctx, h) { // 青龍偃月刀
    pole(ctx, -26, 40, 3.4, h.c.pole);
    poly(ctx, [[-26, -2], [-33, 0], [-26, 2]], h.c.metal, 1.6);
    ctx.beginPath();
    ctx.moveTo(36, -2.5); ctx.lineTo(60, -3);
    ctx.quadraticCurveTo(70, -4, 73, -11);
    ctx.quadraticCurveTo(73, 6, 58, 11);
    ctx.quadraticCurveTo(46, 13, 36, 6);
    ctx.closePath(); ink(ctx, '#dfe5ea');
    ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.lineWidth = 1.3;
    ctx.beginPath(); ctx.moveTo(41, 7.5); ctx.quadraticCurveTo(60, 9, 69, -3); ctx.stroke();
    ctx.strokeStyle = h.c.hood || '#3f8a55'; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.moveTo(40, 0.5); ctx.quadraticCurveTo(50, 3, 58, 1); ctx.stroke();
    dot(ctx, 36, 1.5, 4.6, h.c.trim, 1.6);
    caps(ctx, [[[34, -3], [30, -11], 2.4, h.c.plume], [[36, -3], [35, -12], 2.4, h.c.plume]]);
  },
  spear(ctx, h) { // 槍
    pole(ctx, -26, 50, 2.8, h.c.pole);
    const r = h.c.plume;
    dot(ctx, 47, 2.5, 3.2, r, 1.4); dot(ctx, 45, -1.5, 3, r, 1.4); dot(ctx, 49, -0.5, 2.8, r, 1.4);
    caps(ctx, [[[50, -3.2], [50, 3.2], 2.6, h.c.trim]]);
    ctx.beginPath(); ctx.moveTo(51, 0); ctx.quadraticCurveTo(59, -5, 70, 0); ctx.quadraticCurveTo(59, 5, 51, 0); ctx.closePath();
    ink(ctx, '#e6ebf0', 1.8);
  },
  halberd(ctx, h) { // 月牙戟
    pole(ctx, -24, 46, 3.2, h.c.pole);
    ctx.beginPath(); ctx.moveTo(42, 2); ctx.quadraticCurveTo(44, 15, 57, 13); ctx.quadraticCurveTo(49, 9, 50, 2); ctx.closePath(); ink(ctx, '#d6dce2', 1.8);
    ctx.beginPath(); ctx.moveTo(42, -2); ctx.quadraticCurveTo(44, -8, 50, -9); ctx.quadraticCurveTo(46, -5, 47, -2); ctx.closePath(); ink(ctx, '#d6dce2', 1.6);
    ctx.beginPath(); ctx.moveTo(47, 0); ctx.quadraticCurveTo(54, -4.5, 65, 0); ctx.quadraticCurveTo(54, 4.5, 47, 0); ctx.closePath(); ink(ctx, '#e6ebf0', 1.8);
    caps(ctx, [[[40, -2], [36, -10], 2.4, h.c.plume], [[41, -2], [39, -11], 2.4, h.c.plume]]);
  },
  sword(ctx, h) { // 劍（look.knife：短刀，環首、單刃、刀身短一半）
    if (h.knife) {
      ctx.strokeStyle = h.c.metal || '#c9d0d6'; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.arc(-9.5, 0, 3.4, 0, TAU); ctx.stroke();
      caps(ctx, [[[-6, 0], [1, 0], 3.6, '#3a2a22']]);
      caps(ctx, [[[2, -4], [2, 4], 2.6, h.c.trim]]);
      poly(ctx, [[4, -2.4], [19, -2.4], [23, 1.8], [4, 1.8]], '#e3e8ec', 1.8);
      ctx.strokeStyle = '#9aa6b0'; ctx.lineWidth = 0.9; line(ctx, [6, -0.8], [19, -0.8]);
      return;
    }
    dot(ctx, -8.5, 0, 2.4, h.c.trim, 1.4);
    caps(ctx, [[[-7, 0], [1, 0], 3.8, '#3a2a22']]);
    caps(ctx, [[[2, -5.5], [2, 5.5], 3, h.c.trim]]);
    poly(ctx, [[4, -2.2], [33, -2.2], [38, 0], [33, 2.2], [4, 2.2]], '#e3e8ec', 1.8);
    ctx.strokeStyle = '#9aa6b0'; ctx.lineWidth = 0.9; line(ctx, [7, 0], [31, 0]);
  },
  twin(ctx, h) { // 雙鐵戟（單支）
    pole(ctx, -10, 26, 3.4, h.c.pole);
    ctx.beginPath(); ctx.moveTo(19, 1.5); ctx.quadraticCurveTo(21, 16, 34, 13); ctx.quadraticCurveTo(27, 8, 29, 1.5); ctx.closePath(); ink(ctx, '#9aa1a8', 1.8);
    ctx.beginPath(); ctx.moveTo(27, 0); ctx.quadraticCurveTo(32, -3.5, 39, 0); ctx.quadraticCurveTo(32, 3.5, 27, 0); ctx.closePath(); ink(ctx, '#b3b9bf', 1.6);
    caps(ctx, [[[-10, 0], [-13, 0], 4.5, h.c.metal]]);
  },
  snake(ctx, h) { // 丈八蛇矛
    pole(ctx, -26, 50, 2.9, h.c.pole);
    dot(ctx, 47, 2.5, 3.2, h.c.plume, 1.4); dot(ctx, 45, -1.5, 3, h.c.plume, 1.4);
    ctx.beginPath(); ctx.moveTo(50, -1.8);
    ctx.bezierCurveTo(54, -6, 57, 3, 61, -2.2); ctx.bezierCurveTo(64, -5.5, 67, 0.5, 74, 0);
    ctx.bezierCurveTo(67, 3, 64, 1.5, 61, 2.4); ctx.bezierCurveTo(57, 6.5, 54, -1, 50, 1.8);
    ctx.closePath(); ink(ctx, '#e6ebf0', 1.6);
  },
  axe(ctx, h) { // 大斧
    pole(ctx, -24, 40, 3.6, h.c.pole);
    ctx.beginPath(); ctx.moveTo(25, 2); ctx.quadraticCurveTo(22, 21, 40, 21); ctx.quadraticCurveTo(47, 11, 42, 2); ctx.closePath(); ink(ctx, '#bcc3ca');
    ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.moveTo(27, 17); ctx.quadraticCurveTo(38, 21, 44, 12); ctx.stroke();
    poly(ctx, [[30, -2], [34, -10], [38, -2]], '#bcc3ca', 1.6);
    poly(ctx, [[40, -2.4], [49, 0], [40, 2.4]], '#dfe5ea', 1.6);
  },
  bow(ctx, h) { // 弓：+x 為上弓臂，弓背朝前(+y)
    ctx.lineCap = 'round';
    const arc = () => { ctx.beginPath(); ctx.moveTo(-28, -9); ctx.quadraticCurveTo(-14, 5, 0, 2); ctx.quadraticCurveTo(14, 5, 28, -9); };
    ctx.strokeStyle = '#e8e2d0'; ctx.lineWidth = 0.9; line(ctx, [-28, -9], [28, -9]);
    arc(); ctx.strokeStyle = INK; ctx.lineWidth = 3.4 + OL * 2; ctx.stroke();
    arc(); ctx.strokeStyle = h.c.pole; ctx.lineWidth = 3.4; ctx.stroke();
    caps(ctx, [[[-4, 1.5], [4, 1.5], 4, h.c.trim]]);
  },
  fan(ctx, h) { // 羽扇
    caps(ctx, [[[-5, 0], [6, 0], 2.8, '#6b4a2a']]);
    ctx.beginPath(); ctx.moveTo(6, 0); ctx.lineTo(26, -11); ctx.quadraticCurveTo(32, 0, 26, 11); ctx.closePath(); ink(ctx, h.c.fan || '#f3efe4');
    ctx.strokeStyle = 'rgba(27,20,17,.35)'; ctx.lineWidth = 0.8;
    for (let i = -2; i <= 2; i++) line(ctx, [8, 0], [27, i * 4.6]);
    dot(ctx, 6, 0, 2.2, h.c.trim, 1.2);
  },
  staff(ctx, h) { // 法杖
    pole(ctx, -26, 32, 3, h.c.pole);
    caps(ctx, [[[32, -3.5], [38, -9], 2.2, h.c.trim], [[32, 3.5], [38, 9], 2.2, h.c.trim]]);
    ctx.save(); ctx.globalCompositeOperation = 'lighter';
    const g = ctx.createRadialGradient(40, 0, 0, 40, 0, 12); g.addColorStop(0, h.fx); g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(40, 0, 12, 0, TAU); ctx.fill(); ctx.restore();
    dot(ctx, 40, 0, 5, h.fx, 1.6);
  },
  // ---------- 專屬兵器外型（look.wart，只在畫面層；兵器類型 weapon 照舊，動作與判定不變） ----------
  whip(ctx, h) { // 鐵鞭（竹節鋼鞭，黃蓋）
    dot(ctx, -8.5, 0, 2.6, h.c.trim, 1.4);
    caps(ctx, [[[-7, 0], [1, 0], 3.8, '#3a2a22']]);
    caps(ctx, [[[2, -4.5], [2, 4.5], 3, h.c.trim]]);
    caps(ctx, [[[4, 0], [40, 0], 3.6, '#5d646b']]);
    for (let x = 8; x <= 36; x += 5.6) dot(ctx, x, 0, 2.9 - (x - 8) * 0.025, '#7d858c', 1.2); // 竹節
    dot(ctx, 41, 0, 2.2, '#7d858c', 1.2);
  },
  sanjie(ctx, h) { // 三節棍（凌統）：三節以鐵環相連，後兩節垂擺
    const seg = (a, b) => { caps(ctx, [[a, b, 3.2, h.c.pole]]); caps(ctx, [[a, [a[0] + (b[0] - a[0]) * 0.12, a[1] + (b[1] - a[1]) * 0.12], 3.6, h.c.metal || '#b9c1c8'], [[b[0] - (b[0] - a[0]) * 0.12, b[1] - (b[1] - a[1]) * 0.12], b, 3.6, h.c.metal || '#b9c1c8']]); };
    seg([-6, 0], [18, 0]); seg([21, 1.5], [39, 8]); seg([42, 8.5], [60, 3]);
    ctx.strokeStyle = h.c.metal || '#b9c1c8'; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.arc(19.5, 0.8, 1.8, 0, TAU); ctx.stroke(); ctx.beginPath(); ctx.arc(40.5, 8.3, 1.8, 0, TAU); ctx.stroke();
  },
  brush(ctx, h) { // 紫毫筆（鍾繇）
    caps(ctx, [[[-16, 0], [26, 0], 3.4, '#b9935a']]);
    ctx.strokeStyle = 'rgba(27,20,17,.35)'; ctx.lineWidth = 0.8; for (const x of [-6, 6, 18]) line(ctx, [x, -1.6], [x, 1.6]); // 竹節
    caps(ctx, [[[-16, 0], [-19, 0], 3.8, h.c.trim]]);
    ctx.beginPath(); ctx.moveTo(26, -3); ctx.quadraticCurveTo(36, -3.2, 42, 0); ctx.quadraticCurveTo(36, 3.2, 26, 3); ctx.closePath(); ink(ctx, '#6a4a8a', 1.6); // 紫毫
    ctx.beginPath(); ctx.moveTo(36, -1.6); ctx.quadraticCurveTo(39.5, -1, 42, 0); ctx.quadraticCurveTo(39.5, 1, 36, 1.6); ctx.closePath(); ctx.fillStyle = INK; ctx.fill(); // 沾墨的筆尖
  },
  cards(ctx, h) { // 謎題牌（楊修）：手上扇開三張，青、紅、金
    [['#7fd8ff', -0.42], ['#ff7a5a', 0], ['#ffd46b', 0.42]].forEach(([col, r]) => {
      ctx.save(); ctx.rotate(r as number);
      poly(ctx, [[2, -5], [19, -5], [19, 5], [2, 5]], '#f3ead2', 1.4);
      poly(ctx, [[5, -2.8], [16, -2.8], [16, 2.8], [5, 2.8]], col as string, 0.9);
      ctx.restore();
    });
  },
  dao(ctx, h) { // 古錠刀（孫堅）：單刃、刀身寬、刀尖斜削，環首繫赤纓
    ctx.strokeStyle = h.c.metal || '#c9d0d6'; ctx.lineWidth = 1.8; ctx.beginPath(); ctx.arc(-10, 0, 3.4, 0, TAU); ctx.stroke();
    caps(ctx, [[[-13, 2], [-17, 7], 2.2, '#c23b2e']]);
    caps(ctx, [[[-6.5, 0], [1, 0], 3.8, '#3a2a22']]);
    dot(ctx, 2.5, 0, 4.2, h.c.trim, 1.6);
    ctx.beginPath(); ctx.moveTo(5, -2.6); ctx.lineTo(34, -3.4); ctx.lineTo(40, -1); ctx.quadraticCurveTo(37, 5.5, 30, 6); ctx.quadraticCurveTo(16, 5, 5, 3); ctx.closePath(); ink(ctx, '#e3e8ec', 1.8);
    ctx.strokeStyle = '#9aa6b0'; ctx.lineWidth = 0.9; line(ctx, [8, -0.8], [33, -1.6]);
  },
  xbow(ctx, h) { // 守城弩（郝昭）：側面看，弩身沿 +x、前端的弓臂豎起來（射擊時 xbowShot 會把它端平）
    caps(ctx, [[[-12, 0], [26, 0], 4.2, h.c.pole]]);
    caps(ctx, [[[-2, 2], [-5, 7.5], 2.4, '#3a2a22']]); // 扳機
    ctx.strokeStyle = '#e8e2d0'; ctx.lineWidth = 0.9; line(ctx, [20, -13], [6, 0]); line(ctx, [20, 13], [6, 0]);
    const prod = () => { ctx.beginPath(); ctx.moveTo(20, -13); ctx.quadraticCurveTo(29, 0, 20, 13); };
    prod(); ctx.strokeStyle = INK; ctx.lineWidth = 3.2 + OL * 2; ctx.stroke();
    prod(); ctx.strokeStyle = h.c.metal || '#8a9097'; ctx.lineWidth = 3.2; ctx.stroke();
    caps(ctx, [[[26, -2.5], [26, 2.5], 3, h.c.trim]]);
  },
};
function drawWeapon(ctx, h, hand, ang) {
  const d = down(ang);
  ctx.save(); ctx.translate(hand[0], hand[1]); ctx.rotate(Math.atan2(d[1], d[0]));
  ((h.legend && SG.LEGEND_W && SG.LEGEND_W[h.legend]) || (h.wart && WEAPONS[h.wart]) || WEAPONS[h.weapon])(ctx, h); // 傳說級造型換兵器外型（render/skinfx.ts）；look.wart：專屬兵器外型
  ctx.restore();
}

// ---------- 身體部件 ----------
function drawLeg(ctx, h, L, back) {
  const c = h.c, b = h.bulk || 1, k = back ? 0.72 : 1;
  const boot = shade(c.boot, k);
  caps(ctx, [
    [L.o, L.j, 8.5 * b, shade(c.pants, k)],
    [L.j, L.e, 7 * b, boot],
    [L.e, [L.e[0] + 6 * b, L.e[1] + 0.5], 5.5 * b, boot],
  ]);
}

function drawArm(ctx, h, A, ang, back) {
  const c = h.c, b = h.bulk || 1, k = back ? 0.72 : 1, robe = !!h.robe;
  caps(ctx, [
    [A.o, A.j, (robe ? 9.5 : 7.5) * b, shade(c.sleeve || c.cloth, k)],
    [A.j, A.e, (robe ? 8.5 : 6.5) * b, shade(robe ? c.cloth : c.armor, k)],
  ]);
  dot(ctx, A.e[0], A.e[1], 3.8 * Math.sqrt(b), shade(h.skin, k), 1.8);
  if (!robe) { // 肩甲
    const d = down(ang), p = add(A.o, d, 3), rot = Math.atan2(d[1], d[0]);
    ctx.beginPath(); ctx.ellipse(p[0], p[1], 7.5 * b, 5.8 * b, rot, 0, TAU);
    ink(ctx, shade(c.armor, k));
    ctx.strokeStyle = shade(c.trim, k); ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.ellipse(p[0], p[1], 5 * b, 3.6 * b, rot, 0.3, Math.PI - 0.3); ctx.stroke();
  }
}

// 腰間兩條飄帶：跟著跑動的彈簧往後甩，停下來會回盪
function drawTassels(ctx, h, hip, P, time) {
  const c = h.c, d = P.drag || 0, col = c.trim || c.sleeve || c.cloth;
  for (const [ox, len, ph] of [[-7, 22, 0], [-11, 18, 1.7]]) {
    const w = Math.sin(time * 0.007 + ph) * 2.5, bx = hip[0] + ox, by = hip[1] + 4;
    const mx = bx - 3 - d * 0.5 + w * 0.5, my = by + len * 0.5 - Math.max(0, d) * 0.15;
    const ex = bx - 5 - d * 1.1 + w, ey = by + len - Math.max(0, d) * 0.45;
    caps(ctx, [[[bx, by], [mx, my], 3, col], [[mx, my], [ex, ey], 2.4, col]]);
  }
}

function drawCape(ctx, h, hip, neck, n, P, time) {
  const w = Math.sin(time * 0.005) * 3;
  const flow = Math.max(0, P.lean) * 0.5 + P.flow;
  const a = add(neck, n, 1), b = add(neck, n, -8);
  const bb = [hip[0] - 16 - flow - w, hip[1] + 20 - flow * 0.4];
  const bf = [hip[0] - 4, hip[1] + 22];
  ctx.beginPath();
  ctx.moveTo(a[0], a[1]);
  ctx.quadraticCurveTo(hip[0] + 2, hip[1] - 4, bf[0], bf[1]);
  ctx.quadraticCurveTo((bf[0] + bb[0]) / 2, bb[1] + 4 + w, bb[0], bb[1]);
  ctx.quadraticCurveTo(b[0] - 10 - flow * 0.6, (b[1] + bb[1]) / 2, b[0], b[1]);
  ctx.closePath();
  ink(ctx, h.c.cape);
  ctx.beginPath(); ctx.moveTo(b[0] - 2, b[1] + 4);
  ctx.quadraticCurveTo(b[0] - 10 - flow * 0.6, (b[1] + bb[1]) / 2, bb[0] + 3, bb[1] - 2);
  ctx.lineWidth = 2; ctx.strokeStyle = shade(h.c.cape, 0.7); ctx.stroke();
}

function drawSkirt(ctx, h, hip, td, n, LF, LB) {
  const b = h.bulk || 1, c = h.c;
  const w = add(hip, td, 3);
  const len = h.robe ? 22 : 12;
  const fx = hip[0] + 10 * b + (LF.j[0] - hip[0]) * 0.5;
  const bx = hip[0] - 11 * b + (LB.j[0] - hip[0]) * 0.5;
  const y = hip[1] + len;
  poly(ctx, [add(w, n, -10 * b), add(w, n, 9.5 * b), [fx + 2, y - 1], [bx - 2, y]], c.cloth);
  ctx.lineWidth = 1.3; ctx.strokeStyle = shade(c.cloth, 0.72);
  line(ctx, add(w, n, -4 * b), [(bx + fx) / 2 - 4, y - 1]);
  line(ctx, add(w, n, 4 * b), [(bx + fx) / 2 + 5, y - 1]);
  ctx.lineWidth = 2.4; ctx.strokeStyle = c.trim;
  line(ctx, [bx - 1, y - 1.2], [fx + 1, y - 2.2]);
}

function drawTorso(ctx, h, hip, neck, td, n) {
  const c = h.c, b = h.bulk || 1;
  const pts = [add(hip, n, -10 * b), add(hip, n, 9 * b), add(neck, n, 9.5 * b), add(neck, n, -10.5 * b)];
  path(ctx, pts); ctx.fillStyle = h.robe ? c.cloth : c.armor; ctx.fill();
  ctx.save(); path(ctx, pts); ctx.clip(); ctx.lineCap = 'round';
  if (h.robe) { // 交領右衽
    ctx.strokeStyle = c.armor; ctx.lineWidth = 5; line(ctx, add(neck, n, 5 * b), add(add(hip, td, 9), n, -4));
    ctx.strokeStyle = c.trim; ctx.lineWidth = 2.4; line(ctx, add(neck, n, 8 * b), add(add(hip, td, 6), n, -3));
  } else { // 札甲橫紋與側光
    ctx.strokeStyle = shade(c.armor, 0.72); ctx.lineWidth = 1.2;
    for (let k = 7; k <= 19; k += 4) { const m = add(hip, td, k); line(ctx, add(m, n, -12 * b), add(m, n, 11 * b)); }
    ctx.strokeStyle = shade(c.armor, 1.25); ctx.lineWidth = 3;
    line(ctx, add(add(hip, td, 4), n, 7.5 * b), add(neck, n, 8 * b));
  }
  ctx.restore();
  path(ctx, pts); ink(ctx, null);
  caps(ctx, [[add(neck, n, -5 * b), add(neck, n, 6 * b), 4, h.robe ? c.trim : (c.sleeve || c.cloth)]]);
  if (!h.robe) { // 護心鏡
    const m = add(add(hip, td, 14), n, 4 * b);
    dot(ctx, m[0], m[1], 4.6 * b, c.metal, 1.8);
    ctx.fillStyle = 'rgba(255,255,255,.7)'; ctx.beginPath(); ctx.arc(m[0] + 1.2, m[1] - 1.4, 1.4, 0, TAU); ctx.fill();
  }
  const w = add(hip, td, 2.5);
  caps(ctx, [[add(w, n, -10.5 * b), add(w, n, 9.5 * b), 4.6, c.belt]]);
  const bk = add(w, n, 5 * b); dot(ctx, bk[0], bk[1], 2.8, c.trim, 1.5);
}

// ---------- 頭部 ----------
function drawFace(ctx, h, x, y, P) {
  const ex = x + 8, ey = y + 1;
  ctx.lineCap = 'round'; ctx.strokeStyle = INK; ctx.fillStyle = INK;
  if (h.eye === 'narrow') { // 丹鳳眼、臥蠶眉
    ctx.lineWidth = 2.2; ctx.beginPath(); ctx.moveTo(ex + 3.5, ey); ctx.quadraticCurveTo(ex, ey - 0.6, ex - 3, ey - 2.6); ctx.stroke();
    ctx.lineWidth = 2.8; line(ctx, [ex + 4.5, ey - 5], [ex - 4, ey - 8.5]);
  } else {
    const fierce = h.eye === 'fierce';
    if (h.eyeColor) ctx.fillStyle = h.eyeColor;
    ctx.beginPath(); ctx.ellipse(ex, ey, fierce ? 2 : 2.4, fierce ? 2.8 : 3.5, 0, 0, TAU); ctx.fill();
    if (h.eyeColor) { ctx.lineWidth = 1; ctx.stroke(); }
    ctx.fillStyle = '#ffffff'; ctx.beginPath(); ctx.arc(ex + 0.7, ey - 1.2, 0.9, 0, TAU); ctx.fill();
    ctx.lineWidth = fierce ? 2.8 : 1.8;
    if (fierce) line(ctx, [ex - 4, ey - 7.5], [ex + 4, ey - 4]);
    else line(ctx, [ex - 3, ey - 6], [ex + 3.5, ey - 6.5]);
    if (h.female) { ctx.lineWidth = 1.4; line(ctx, [ex + 1, ey - 3.2], [ex + 4.2, ey - 5]); }
  }
  if (h.patch) { // 眼罩
    ctx.lineWidth = 1.6; ctx.strokeStyle = INK; line(ctx, [x - 17, y - 7], [x + 15, y - 3]);
    ctx.beginPath(); ctx.ellipse(ex, ey, 4.2, 4.6, 0, 0, TAU); ctx.fillStyle = '#1c1b24'; ctx.fill(); ctx.stroke();
  }
  ctx.lineWidth = 1.5; ctx.strokeStyle = INK;
  if (h.female && P.mouth <= 0.5) {
    ctx.fillStyle = '#c0453c'; ctx.beginPath(); ctx.ellipse(x + 12, y + 9, 1.8, 1.2, 0, 0, TAU); ctx.fill();
  } else if (P.mouth > 0.5) {
    ctx.fillStyle = '#5a1c16'; ctx.beginPath(); ctx.ellipse(x + 12, y + 9, 2.2, 3, 0, 0, TAU); ctx.fill(); ctx.stroke();
  } else line(ctx, [x + 10, y + 9], [x + 13, y + 9]);
}

function drawBeard(ctx, h, x, y, sway) {
  const col = h.beardColor || h.hair;
  ctx.lineCap = 'round';
  if (h.beard === 'long') { // 美髯
    ctx.beginPath(); ctx.moveTo(x - 1, y + 6);
    ctx.quadraticCurveTo(x + 6, y + 14, x + 15, y + 10);
    ctx.quadraticCurveTo(x + 16, y + 26, x + 9 + sway * 2, y + 44);
    ctx.quadraticCurveTo(x + 2, y + 30, x - 1, y + 6);
    ctx.closePath(); ink(ctx, col);
    ctx.strokeStyle = shade(col, 1.35); ctx.lineWidth = 0.8;
    for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.moveTo(x + 5 + i * 3, y + 14); ctx.quadraticCurveTo(x + 9 + i * 2, y + 26, x + 8 + i + sway * 2, y + 36 - i * 3); ctx.stroke(); }
    ctx.strokeStyle = col; ctx.lineWidth = 2.4;
    ctx.beginPath(); ctx.moveTo(x + 9, y + 7); ctx.quadraticCurveTo(x + 15, y + 6, x + 18, y + 11); ctx.stroke();
  } else if (h.beard === 'goatee') {
    poly(ctx, [[x + 9, y + 11], [x + 15, y + 10], [x + 12.5, y + 21]], col, 1.6);
    ctx.strokeStyle = col; ctx.lineWidth = 1.6;
    ctx.beginPath(); ctx.moveTo(x + 10, y + 7); ctx.quadraticCurveTo(x + 15, y + 6, x + 17, y + 9); ctx.stroke();
  } else if (h.beard === 'mustache') {
    ctx.strokeStyle = col; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x + 9, y + 7); ctx.quadraticCurveTo(x + 14, y + 5.5, x + 17, y + 8); ctx.stroke();
  } else if (h.beard === 'bushy') { // 虯髯
    ctx.beginPath(); ctx.moveTo(x - 3, y + 2);
    ctx.quadraticCurveTo(x - 1, y + 19, x + 10, y + 19);
    ctx.quadraticCurveTo(x + 18, y + 17, x + 17, y + 8);
    ctx.quadraticCurveTo(x + 9, y + 13, x + 2, y + 8);
    ctx.closePath(); ink(ctx, col);
    for (let i = 0; i < 4; i++) poly(ctx, [[x + 1 + i * 4, y + 17], [x + 3 + i * 4, y + 23], [x + 5 + i * 4, y + 17]], col, 1.2);
  }
}

function drawHeadgear(ctx, h, x, y, r, sway) {
  const g = h.c;
  switch (h.head) {
    case 'hood': { // 綠巾
      ctx.beginPath(); ctx.arc(x, y, r + 1.6, Math.PI + 0.45, TAU - 0.45); ctx.closePath(); ink(ctx, g.hood);
      caps(ctx, [
        [[x - r, y - 6], [x - r - 9 - sway * 3, y + 6], 3.6, g.hood],
        [[x - r + 1, y - 5], [x - r - 4 - sway * 2, y + 12], 3.2, shade(g.hood, 0.8)],
      ]);
      dot(ctx, x - r + 1, y - 8, 4.2, g.hood);
      ctx.strokeStyle = shade(g.hood, 0.7); ctx.lineWidth = 1.2; line(ctx, [x - 10, y - 13], [x + 8, y - 14]);
      break;
    }
    case 'helmet': { // 盔與纓
      const p0 = [x - 1, y - r - 5], p1 = [x - 4, y - r - 13], p2 = [x - 13, y - r - 15 + sway * 1.5], p3 = [x - 22, y - r - 9 + sway * 3];
      caps(ctx, [[p0, p1, 4.5, g.plume], [p1, p2, 6, g.plume], [p2, p3, 4, g.plume]]);
      poly(ctx, [[x - r + 2, y - 7], [x - r - 4, y - 6], [x - r - 6, y + 10], [x - r + 3, y + 8]], shade(g.metal, 0.8));
      ctx.beginPath(); ctx.arc(x, y, r + 2.5, Math.PI + 0.42, TAU - 0.42); ctx.closePath(); ink(ctx, g.metal);
      ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(x + 1, y, r - 1, -2.2, -1.5); ctx.stroke();
      if (h.horns) caps(ctx, [[[x + 3, y - r], [x + 12, y - r - 8], 3, g.trim], [[x - 6, y - r], [x - 13, y - r - 7], 2.6, g.trim]]);
      caps(ctx, [[[x - r - 1, y - 7.5], [x + r + 2, y - 8], 3.6, g.trim]]);
      dot(ctx, x - 1, y - r - 3, 2.6, g.trim, 1.5);
      break;
    }
    case 'guan': { // 束髮小冠
      dot(ctx, x - 3, y - r - 1, 6.5, h.hair);
      poly(ctx, [[x - 8, y - r + 1], [x + 2, y - r + 1], [x + 1, y - r - 8], [x - 7, y - r - 8]], g.trim, 1.6);
      caps(ctx, [[[x - 13, y - r - 4], [x + 7, y - r - 3], 1.8, '#efe3b8']]);
      break;
    }
    case 'crown': { // 玄冠
      poly(ctx, [[x - 13, y - 8], [x + 11, y - 8], [x + 8, y - 27], [x - 12, y - 25]], '#1c1b24');
      caps(ctx, [[[x - 13, y - 9], [x + 11, y - 9], 3.2, g.trim], [[x - 18, y - 28], [x + 14, y - 29.5], 3.4, '#1c1b24']]);
      caps(ctx, [[[x + 13, y - 28], [x + 13, y - 20], 1.2, '#e8d9a8']]);
      dot(ctx, x + 4, y - 17, 2.6, '#7fd1b0', 1.4);
      break;
    }
    case 'headband': { // 短刺髮與紅頭巾
      for (let i = 0; i < 4; i++) poly(ctx, [[x - 12 + i * 6, y - 13], [x - 10 + i * 6, y - 22 + (i % 2) * 2], [x - 6 + i * 6, y - 14]], h.hair, 1.4);
      caps(ctx, [
        [[x - r, y - 8], [x - r - 9 - sway * 3, y - 2], 3, g.band],
        [[x - r, y - 7], [x - r - 6 - sway * 2, y + 6], 3, shade(g.band, 0.8)],
        [[x - r + 1, y - 8], [x + r - 1, y - 6], 4.6, g.band],
      ]);
      break;
    }
    case 'lunjin': { // 綸巾
      caps(ctx, [
        [[x - 11, y - 10], [x - 24 - sway * 3, y + 6], 2.6, shade(g.hood, 0.85)],
        [[x - 11, y - 9], [x - 20 - sway * 2, y + 14], 2.4, g.hood],
      ]);
      poly(ctx, [[x - 13, y - 7], [x + 12, y - 7], [x + 7, y - 31], [x - 10, y - 28]], g.hood);
      ctx.strokeStyle = shade(g.hood, 0.7); ctx.lineWidth = 1.2;
      line(ctx, [x - 2, y - 8], [x - 1, y - 29]); line(ctx, [x + 5, y - 8], [x + 5, y - 29]);
      caps(ctx, [[[x - 13, y - 8], [x + 12, y - 8], 3, g.trim]]);
      break;
    }
    case 'bun': { // 雙髻
      dot(ctx, x - 7, y - r + 1, 6.5, h.hair);
      dot(ctx, x + 3, y - r - 2, 5.8, h.hair);
      caps(ctx, [[[x - 14, y - r - 5], [x + 2, y - r + 3], 1.6, g.trim]]);
      dot(ctx, x + 6, y - r + 1, 2.6, g.flower || '#f08aa8', 1.2);
      break;
    }
    case 'pony': { // 束馬尾
      caps(ctx, [[[x - 10, y - r + 2], [x + 4, y - r + 1], 3.2, g.trim]]);
      dot(ctx, x - 9, y - r + 1, 3.6, h.hair, 1.4);
      break;
    }
    case 'topknot': { // 光頭小髻
      dot(ctx, x - 3, y - r - 2, 4.6, h.hair, 1.6);
      caps(ctx, [[[x - 7, y - r + 1], [x + 1, y - r], 2, g.trim]]);
      break;
    }
    case 'cap': { // 小兵尖盔
      poly(ctx, [[x - r - 3, y - 6], [x + r + 3, y - 6], [x + 1, y - r - 10]], g.metal);
      dot(ctx, x + 1, y - r - 10, 2.4, g.plume, 1.4);
      break;
    }
  }
}

// 節慶限定造型的頭飾：取代原本的頭盔／冠帽（臉朝 +x，帽尖與耳朵往後擺）
function drawAcc(ctx, h, x, y, r, sway) {
  const g = h.c;
  switch (h.acc) {
    case 'witch': { // 萬聖：彎尖的巫師帽＋帽帶
      const tip = [x - 20 - sway * 3, y - r - 30 + sway * 1.5];
      ctx.beginPath(); ctx.moveTo(x - 12, y - r + 2); ctx.quadraticCurveTo(x - 6, y - r - 18, tip[0], tip[1]);
      ctx.quadraticCurveTo(x - 2, y - r - 22, x + 11, y - r + 2); ctx.closePath(); ink(ctx, g.hood);
      ctx.beginPath(); ctx.ellipse(x - 1, y - r + 3, r + 10, 5, -0.04, 0, TAU); ink(ctx, g.hood);
      caps(ctx, [[[x - 11, y - r - 1], [x + 10, y - r - 1], 3.2, g.trim]]);
      poly(ctx, [[x - 3, y - r - 4], [x + 3, y - r - 4], [x + 3, y - r + 2], [x - 3, y - r + 2]], g.metal, 1.2);
      dot(ctx, tip[0], tip[1], 2.4, g.trim, 1.2);
      break;
    }
    case 'santa': { // 聖誕：往後垂的紅帽＋白絨邊與絨球
      const tip = [x - r - 12 - sway * 3, y + 2 + sway * 1.5];
      ctx.beginPath(); ctx.moveTo(x - r + 1, y - 12); ctx.quadraticCurveTo(x - 4, y - r - 22, x + r - 1, y - 13);
      ctx.quadraticCurveTo(x - 8, y - r - 10, tip[0], tip[1]); ctx.closePath(); ink(ctx, g.hood);
      caps(ctx, [[[x - r, y - 11], [x + r, y - 12.5], 5, '#f6f6f6']]);
      dot(ctx, tip[0], tip[1], 4.6, '#f6f6f6', 1.4);
      break;
    }
    case 'wusha': { // 新春：財神官帽，兩側帽翅隨步伐上下晃
      const fl = sway * 1.2;
      caps(ctx, [[[x - r - 16, y - r - 4 + fl], [x - r + 2, y - r - 4], 1.6, INK], [[x + r - 2, y - r - 4], [x + r + 16, y - r - 4 - fl], 1.6, INK]]);
      ctx.beginPath(); ctx.ellipse(x - r - 18, y - r - 4 + fl, 6, 3.6, 0, 0, TAU); ink(ctx, g.hood, 1.4);
      ctx.beginPath(); ctx.ellipse(x + r + 18, y - r - 4 - fl, 6, 3.6, 0, 0, TAU); ink(ctx, g.hood, 1.4);
      ctx.beginPath(); ctx.arc(x, y - 4, r + 2, Math.PI + 0.35, TAU - 0.35); ctx.closePath(); ink(ctx, g.hood);
      poly(ctx, [[x - 9, y - r - 3], [x + 9, y - r - 3], [x + 7, y - r - 13], [x - 7, y - r - 13]], g.hood);
      caps(ctx, [[[x - r - 1, y - 8], [x + r + 1, y - 9], 3.4, g.trim]]);
      dot(ctx, x + 1, y - r - 7, 3, g.trim, 1.2);
      break;
    }
    case 'pumpkin': { // 萬聖：南瓜燈頭套，正面刻鬼臉、裡面透出燭光
      const cx = x - 1, cy = y - r - 3;
      ctx.beginPath(); ctx.ellipse(cx, cy, r + 6, 12, 0, 0, TAU); ink(ctx, g.armor);
      ctx.strokeStyle = shade(g.armor, -0.2); ctx.lineWidth = 1;
      for (const k of [-0.5, 0.5]) { ctx.beginPath(); ctx.ellipse(cx + k * (r + 6), cy, 6, 11, 0, 0, TAU); ctx.stroke(); }
      caps(ctx, [[[cx - 1, cy - 11], [cx - 4 - sway, cy - 18], 3.4, g.trim]]);
      // 鬼臉只填色不描邊，才不會看成皇冠
      const glow = (pts) => { path(ctx, pts); ctx.fillStyle = '#ffe066'; ctx.fill(); };
      glow([[x + 3, cy - 1], [x + 10, cy - 1], [x + 6.5, cy - 7]]);
      glow([[x + 13, cy - 1], [x + 20, cy - 1], [x + 16.5, cy - 7]]);
      glow([[x + 2, cy + 3], [x + 21, cy + 3], [x + 18, cy + 8], [x + 15, cy + 5.5], [x + 11.5, cy + 8.5], [x + 8, cy + 5.5], [x + 5, cy + 8]]);
      break;
    }
    case 'horns': { // 萬聖：夜叉雙角（粗根、往外彎、尖端發亮）
      const horn = (bx, dir, len) => {
        ctx.beginPath(); ctx.moveTo(bx - 4.5, y - r + 4);
        ctx.quadraticCurveTo(bx + dir * 9, y - r - len * 0.3, bx + dir * 7, y - r - len);
        ctx.quadraticCurveTo(bx + dir * 3, y - r - len * 0.35, bx + 4.5, y - r + 4); ctx.closePath(); ink(ctx, shade(g.metal, -0.2), 1.6);
        dot(ctx, bx + dir * 6.5, y - r - len + 2.5, 1.6, g.metal, 0.8);
      };
      horn(x - 8, -1, 12); horn(x + 5, 1, 14);
      caps(ctx, [[[x - r, y - 9], [x + r, y - 10], 2.6, g.trim]]);
      break;
    }
    case 'jiangshi': { // 萬聖：清朝殭屍官帽，額前貼一張黃符
      ctx.beginPath(); ctx.ellipse(x, y - r + 2, r + 8, 5, -0.04, 0, TAU); ink(ctx, g.hood);
      ctx.beginPath(); ctx.arc(x, y - r + 1, 12, Math.PI, TAU); ctx.closePath(); ink(ctx, g.hood);
      caps(ctx, [[[x - 12, y - r - 1], [x + 12, y - r - 1], 2.4, g.trim]]);
      dot(ctx, x, y - r - 12, 3, g.trim, 1.2);
      const sw = sway * 1.5;
      poly(ctx, [[x + 5, y - r + 5], [x + 14, y - r + 5], [x + 15 + sw, y + 10], [x + 6 + sw, y + 10]], '#f2d24a', 1.2);
      ctx.strokeStyle = '#c8281e'; ctx.lineWidth = 1.3;
      ctx.beginPath(); ctx.moveTo(x + 9.5, y - r + 8); ctx.lineTo(x + 11 + sw * 0.5, y - 6); ctx.lineTo(x + 8 + sw * 0.6, y - 1); ctx.lineTo(x + 12 + sw * 0.8, y + 3); ctx.lineTo(x + 10 + sw, y + 8); ctx.stroke();
      break;
    }
    case 'bunny': { // 中秋：玉兔長耳（粉色內耳），跑動時往後倒
      const ear = (bx, lean, len) => {
        const t = [bx - 6 - lean, y - r - len];
        ctx.beginPath(); ctx.moveTo(bx - 4, y - r + 4); ctx.quadraticCurveTo(bx - 9 - lean * 0.6, y - r - len * 0.6, t[0], t[1]);
        ctx.quadraticCurveTo(bx + 4 - lean * 0.4, y - r - len * 0.6, bx + 4, y - r + 4); ctx.closePath(); ink(ctx, '#f6f3ea');
        ctx.beginPath(); ctx.moveTo(bx - 1.5, y - r + 1); ctx.quadraticCurveTo(bx - 5 - lean * 0.6, y - r - len * 0.55, t[0] + 0.5, t[1] + 5);
        ctx.quadraticCurveTo(bx + 1 - lean * 0.4, y - r - len * 0.55, bx + 1.5, y - r + 1); ctx.closePath(); ctx.fillStyle = g.plume; ctx.fill();
      };
      ear(x - 7, 6 + sway * 3, 26); ear(x + 4, 3 + sway * 2, 30);
      caps(ctx, [[[x - r, y - 9], [x + r, y - 10], 3, g.trim]]);
      dot(ctx, x + 9, y - r + 3, 2.6, '#f2d27a', 1.2);
      break;
    }
  }
}

function drawHead(ctx, h, C, P, time) {
  const x = C[0], y = C[1], r = 17, sway = Math.sin(time * 0.006) + (P.drag || 0) * 0.22; // drag：跑動時頭髮往後甩
  if (h.head === 'guan' || h.head === 'bun') caps(ctx, [
    [[x - 12, y - 6], [x - 19 - sway * 1.5, y + 8], 10, h.hair],
    [[x - 19 - sway * 1.5, y + 8], [x - 22 - sway * 3, y + (h.head === 'bun' ? 32 : 24)], 7, h.hair],
  ]);
  if (h.head === 'pony') caps(ctx, [
    [[x - 10, y - r + 1], [x - 22 - sway * 2, y - 6], 7, h.hair],
    [[x - 22 - sway * 2, y - 6], [x - 26 - sway * 4, y + 12], 5, h.hair],
  ]);
  ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fillStyle = h.skin; ctx.fill();
  if (h.head !== 'topknot') {
    ctx.save(); ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.clip();
    ctx.fillStyle = h.hair;
    ctx.fillRect(x - r - 2, y - r - 2, r - 1, r * 2 + 4);
    ctx.beginPath(); ctx.moveTo(x - r - 2, y - 5); ctx.lineTo(x - 2, y - 6);
    ctx.quadraticCurveTo(x + 6, y - 3, x + r + 2, y - 8);
    ctx.lineTo(x + r + 2, y - r - 2); ctx.lineTo(x - r - 2, y - r - 2); ctx.closePath(); ctx.fill();
    ctx.restore();
  }
  ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ink(ctx, null);
  if (h.bigEars) { ctx.beginPath(); ctx.ellipse(x - 3, y + 4, 4, 6.5, 0, 0, TAU); ink(ctx, h.skin, 1.6); }
  else dot(ctx, x - 3, y + 2, 3.4, h.skin, 1.6);
  drawFace(ctx, h, x, y, P);
  drawBeard(ctx, h, x, y, sway);
  if (h.acc) drawAcc(ctx, h, x, y, r, sway); else drawHeadgear(ctx, h, x, y, r, sway);
}

// ---------- 組裝 ----------
function drawHero(ctx, h, P, time) {
  const c = h.c, b = h.bulk || 1;
  ctx.save();
  ctx.translate(P.px, P.py);
  if (P.spin) { ctx.translate(0, -30); ctx.rotate(P.spin * D); ctx.translate(0, 30); }
  const hip = [0, -30 + P.bob];
  const td = up(P.lean), n = [Math.cos(P.lean * D), Math.sin(P.lean * D)];
  const neck = add(hip, td, 24);
  const headC = add(neck, up(P.lean * 0.6 + P.head), 15);
  const sh = add(neck, td, -4);
  const legF = chain(add(hip, n, 3 * b), P.fl1, 14, P.fl2, 14);
  const legB = chain(add(hip, n, -3 * b), P.bl1, 14, P.bl2, 14);
  const armF = chain(add(sh, n, 3 * b), P.fa1, 11, P.fa2, 11);
  const armB = chain(add(sh, n, -4 * b), P.ba1, 11, P.ba2, 11);

  if (c.cape) drawCape(ctx, h, hip, neck, n, P, time);
  if (h.wclass === 'twin') drawWeapon(ctx, h, armB.e, P.bw);
  drawArm(ctx, h, armB, P.ba1, true);
  drawLeg(ctx, h, legB, true);
  drawLeg(ctx, h, legF, false);
  drawSkirt(ctx, h, hip, td, n, legF, legB);
  drawTassels(ctx, h, hip, P, time);
  drawTorso(ctx, h, hip, neck, td, n);
  if (h.shield) { // 副手盾
    const s = armB.e;
    dot(ctx, s[0] + 3, s[1], 13 * b, c.metal);
    ctx.strokeStyle = c.trim; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(s[0] + 3, s[1], 9 * b, 0, TAU); ctx.stroke();
    dot(ctx, s[0] + 3, s[1], 3.2, c.trim, 1.4);
  }
  drawHead(ctx, h, headC, P, time);
  drawWeapon(ctx, h, armF.e, P.wpn);
  drawArm(ctx, h, armF, P.fa1, false);
  ctx.restore();
}
SG.drawHero = drawHero;

// ---------- 動畫 ----------
const BASE = { px: 0, py: 0, bob: 0, lean: 4, head: 0, fa1: 25, fa2: 75, ba1: -12, ba2: 15, fl1: 12, fl2: 6, bl1: -14, bl2: -10, wpn: 165, bw: 165, spin: 0, mouth: 0, flow: 0 };
SG.POSE_KEYS = Object.keys(BASE);
const STANCE = {
  pole: { fa1: 35, fa2: 80, ba1: 50, ba2: 100, wpn: 168, lean: 4 },
  spear: { fa1: 35, fa2: 82, ba1: 48, ba2: 98, wpn: 158, lean: 4 },
  sword: { fa1: 22, fa2: 68, ba1: -14, ba2: 8, wpn: 128, lean: 3 },
  twin: { fa1: 25, fa2: 72, ba1: 18, ba2: 64, wpn: 150, bw: 158, lean: 7, fl1: 16, bl1: -18 },
  bow: { fa1: 20, fa2: 45, ba1: -10, ba2: 10, wpn: 172, lean: 2 },
  fan: { fa1: 30, fa2: 115, ba1: -14, ba2: 8, wpn: 150, lean: 2 },
  staff: { fa1: 30, fa2: 70, ba1: -10, ba2: 15, wpn: 172, lean: 3 },
};
const mk = (st, o) => Object.assign({}, BASE, st, o);
const K = (t, p, e) => ({ t, p: p || {}, e });
// 每段關鍵影格各自的緩動：io 一般、out 慢慢停住（蓄力）、snap 一開始就全速（出手）、back 收招時稍微超過再回來
const EASE = {
  io: (s) => (s < 0.5 ? 2 * s * s : 1 - Math.pow(-2 * s + 2, 2) / 2),
  out: (s) => 1 - Math.pow(1 - s, 3),
  snap: (s) => 1 - Math.pow(1 - s, 4),
  back: (s) => { const c1 = 1.5, c3 = c1 + 1; return 1 + c3 * Math.pow(s - 1, 3) + c1 * Math.pow(s - 1, 2); },
};
// 出手動作自動套上「蓄力 → 爆發 → 收招回彈」：很短的一段當成出手（snap），它前面那段當成蓄力（out），
// 最後回到站姿那段加一點回彈（back）。手動指定過 e 的關鍵影格不動
function punchUp(a) {
  if (!a || !a.keys || a.keys.length < 3) return a;
  const ks = a.keys;
  for (let i = 1; i < ks.length; i++) {
    if (ks[i].e) continue;
    const dt = ks[i].t - ks[i - 1].t, moved = Object.keys(ks[i].p).length > 0;
    if (i === ks.length - 1) ks[i].e = 'back';
    else if (dt <= 0.17 && moved) { ks[i].e = 'snap'; if (i > 1 && !ks[i - 1].e) ks[i - 1].e = 'out'; }
  }
  return a;
}
SG.EASE = EASE;
const LUNGE = { fl1: 36, fl2: 24, bl1: -36, bl2: -46 };
const L = (o) => Object.assign({}, o, LUNGE);

function sample(keys, u, st) {
  let i = 0;
  while (i < keys.length - 2 && u > keys[i + 1].t) i++;
  const a = keys[i], b = keys[i + 1];
  let s = b.t > a.t ? (u - a.t) / (b.t - a.t) : 1;
  s = s < 0 ? 0 : s > 1 ? 1 : s;
  const e = (EASE[b.e] || EASE.io)(s);
  const o = {};
  for (const k in BASE) {
    const va = k in a.p ? a.p[k] : k in st ? st[k] : BASE[k];
    const vb = k in b.p ? b.p[k] : k in st ? st[k] : BASE[k];
    o[k] = va + (vb - va) * e;
  }
  return o;
}

function idle(st) {
  return (u) => {
    const s = Math.sin(u * TAU);
    return mk(st, { bob: 0.8 + 0.8 * s, head: 1.5 * s, fa1: st.fa1 + 2 * s, ba1: st.ba1 + 2 * s, wpn: st.wpn + 1.5 * s, bw: (st.bw || BASE.bw) + 1.5 * s });
  };
}
function gait(st, run) {
  const A = run ? 36 : 24, KN = run ? 44 : 24;
  return (u) => {
    const ph = u * TAU, s = Math.sin(ph), c = Math.cos(ph);
    const fl1 = 6 + A * s, bl1 = 6 - A * s;
    return mk(st, {
      lean: st.lean + (run ? 14 : 3), bob: (run ? 3.2 : 1.6) * Math.abs(s) - (run ? 1 : 0), head: run ? -6 : 0,
      fl1, fl2: fl1 - KN * Math.max(0, c), bl1, bl2: bl1 - KN * Math.max(0, -c),
      fa1: st.fa1 - 5 * s - (run ? 8 : 0), wpn: st.wpn - 3 * s - (run ? 32 : 0), bw: (st.bw || BASE.bw) + 4 * s - (run ? 30 : 0),
      ba1: st.ba1 + (st.ba1 < 20 ? 24 : 6) * s, flow: run ? 10 : 2,
    });
  };
}
function guard(st, h) {
  return (u) => {
    const s = Math.sin(u * TAU);
    return mk(st, { lean: -4, bob: 3.5 + 0.5 * s, head: -3, fl1: 22, fl2: 10, bl1: -24, bl2: -18, fa1: 62, fa2: 150, wpn: h.wclass === 'sword' ? 172 : 176, ba1: 58, ba2: 140, bw: 210 + s });
  };
}
function slash(st, twin) {
  const w = twin ? { ba1: 150, ba2: 185, bw: 225 } : { ba1: -30, ba2: 0 };
  const s = twin ? { ba1: 70, ba2: 88, bw: 72 } : { ba1: -40, ba2: -20 };
  return {
    dur: 0.52, st,
    keys: [K(0), K(0.3, Object.assign({ lean: -6, fa1: 160, fa2: 195, wpn: 230, fl1: 20, bl1: -18, px: -3 }, w)),
      K(0.44, Object.assign(L({ lean: 24, fa1: 78, fa2: 95, wpn: 65, px: 9, bob: 3 }), s)),
      K(0.72, Object.assign(L({ lean: 20, fa1: 74, fa2: 90, wpn: 60, px: 8, bob: 3 }), s)), K(1)],
    events: [{ t: 0.36, fx: 'swoosh', a1: 230, a2: 60 }],
  };
}
function thrust(st) {
  return {
    dur: 0.48, st,
    keys: [K(0), K(0.3, { lean: -4, fa1: 40, fa2: 62, wpn: 96, px: -4, ba1: 30, ba2: 80 }),
      K(0.44, L({ lean: 18, fa1: 88, fa2: 90, wpn: 92, px: 12, ba1: 70, ba2: 90, bob: 3 })),
      K(0.66, L({ lean: 16, fa1: 86, fa2: 90, wpn: 92, px: 11, ba1: 68, ba2: 90, bob: 3 })), K(1)],
    events: [{ t: 0.4, fx: 'thrust' }],
  };
}
function hit(st) {
  return {
    dur: 0.42, st,
    keys: [K(0), K(0.16, { lean: -22, head: -16, px: -9, fa1: 5, fa2: 35, ba1: -40, ba2: -20, wpn: st.wpn + 20, mouth: 1, bob: 2, fl1: 24, bl1: -6 }),
      K(0.55, { lean: -12, head: -8, px: -7, fa1: 15, fa2: 50, wpn: st.wpn + 10, mouth: 1, bob: 1, fl1: 18 }), K(1)],
  };
}
function jump(st) {
  return {
    dur: 0.7, st,
    keys: [K(0), K(0.18, { bob: 6, lean: 10, fl1: 28, fl2: -8, bl1: -22, bl2: -44 }),
      K(0.5, { py: -52, lean: 2, fl1: 34, fl2: -18, bl1: -4, bl2: -52, fa1: st.fa1 + 20, ba1: st.ba1 + 30, flow: 8 }),
      K(0.82, { py: 0, bob: 5, fl1: 22, fl2: 4, bl1: -22, bl2: -30 }), K(1)],
  };
}
function fall(st) {
  const down1 = { spin: -88, py: 22, px: -16, lean: -10, fa1: 150, fa2: 170, ba1: 130, fl1: 20, bl1: 30, wpn: 200, mouth: 1 };
  return {
    dur: 0.9, hold: true, st,
    keys: [K(0), K(0.25, { lean: -24, head: -20, px: -6, mouth: 1, fa1: -20, ba1: -50, wpn: st.wpn + 40 }), K(0.7, down1), K(1, Object.assign({}, down1, { spin: -90, py: 24 }))],
  };
}

function shot(st) { // 弓：拉弓、放箭
  const aim = { fa1: 90, fa2: 90, wpn: 182, ba1: 82, ba2: 40, lean: 0, head: 2 };
  return {
    dur: 0.6, st,
    keys: [K(0), K(0.35, aim), K(0.5, Object.assign({}, aim, { ba1: 55, ba2: -5, px: -3 })), K(0.75, Object.assign({}, aim, { ba1: 50, ba2: -10, px: -3 })), K(1)],
    events: [{ t: 0.45, fx: 'bolt', shape: 'arrow' }],
  };
}
function cast(st, shape) { // 扇、杖：揮出氣彈
  return {
    dur: 0.55, st,
    keys: [K(0), K(0.3, { fa1: 130, fa2: 150, wpn: 190, lean: -4 }), K(0.5, { fa1: 92, fa2: 95, wpn: 110, lean: 10, px: 4 }), K(0.75, { fa1: 90, fa2: 95, wpn: 112, lean: 8, px: 4 }), K(1)],
    events: [{ t: 0.45, fx: 'bolt', shape }],
  };
}

// ---------- 遠程普攻（v0.3.4）：依兵器各自的出手動作；投射物外觀見 SG.RANGED_SHAPE ----------
function xbowShot(st) { // 弩：雙手平舉、扣扳機，後座一縮
  const aim = { fa1: 88, fa2: 88, wpn: 92, ba1: 80, ba2: 72, lean: 2, head: 2 };
  return {
    dur: 0.6, st, // 動作長度跟以前（弓）一樣，攻擊節奏不變
    keys: [K(0), K(0.32, aim), K(0.44, Object.assign({}, aim, { px: -5, lean: -5, fa1: 96, ba1: 86 })), K(0.7, Object.assign({}, aim, { px: -3, lean: -2 })), K(1)],
    events: [{ t: 0.4, fx: 'bolt', shape: 'xbolt' }],
  };
}
function fanSwing(st) { // 羽扇：反手收到胸前，橫揮出去
  return {
    dur: 0.55, st,
    keys: [K(0), K(0.32, { fa1: 20, fa2: 140, wpn: 250, lean: -8, px: -3, head: -2 }), K(0.48, { fa1: 108, fa2: 100, wpn: 40, lean: 12, px: 6 }), K(0.72, { fa1: 100, fa2: 96, wpn: 48, lean: 9, px: 5 }), K(1)],
    events: [{ t: 0.42, fx: 'bolt', shape: 'wind' }],
  };
}
function staffCast(st) { // 杖：舉杖過頂蓄力，往前一指放出法球
  return {
    dur: 0.55, st,
    keys: [K(0), K(0.36, { fa1: 165, fa2: 175, wpn: 180, ba1: 120, ba2: 150, lean: -6, head: -8 }), K(0.52, L({ fa1: 95, fa2: 92, wpn: 95, ba1: -20, lean: 14, px: 7, bob: 2 })), K(0.76, L({ fa1: 92, fa2: 90, wpn: 98, ba1: -18, lean: 12, px: 6, bob: 2 })), K(1)],
    events: [{ t: 0.5, fx: 'bolt', shape: 'orb' }],
  };
}
function brushStroke(st) { // 筆：提筆一揮，甩出墨跡
  return {
    dur: 0.55, st,
    keys: [K(0), K(0.3, { fa1: 125, fa2: 160, wpn: 205, lean: -3, head: -2 }), K(0.44, { fa1: 70, fa2: 60, wpn: 30, lean: 10, px: 4 }), K(0.7, { fa1: 72, fa2: 64, wpn: 34, lean: 8, px: 3 }), K(1)],
    events: [{ t: 0.4, fx: 'bolt', shape: 'ink' }],
  };
}
function throwArm(st, shape, side) { // 擲：牌、飛刀過肩甩出；飛鏢側身甩腕
  const wind = side ? { fa1: 10, fa2: 150, wpn: 200, ba1: 70, ba2: 90, lean: -6, px: -3 } : { fa1: 200, fa2: 230, wpn: 240, ba1: 60, ba2: 90, lean: -10, px: -4, head: -4 };
  const rel = side ? { fa1: 96, fa2: 92, wpn: 80, ba1: -30, lean: 12, px: 6 } : { fa1: 85, fa2: 80, wpn: 70, ba1: -40, lean: 16, px: 8, bob: 2 };
  return {
    dur: shape === 'card' ? 0.55 : 0.52, st, // 楊修原本是扇（0.55）、祝融與張郃原本是劍（0.52）
    keys: [K(0), K(0.34, wind), K(0.46, L(rel)), K(0.72, L(Object.assign({}, rel, { fa1: rel.fa1 - 6 }))), K(1)],
    events: [{ t: 0.42, fx: 'bolt', shape }],
  };
}
function qiThrust(st, twin) { // 劍：前刺送出劍氣；雙劍左右交替
  const k1 = L({ fa1: 92, fa2: 90, wpn: 90, ba1: twin ? 20 : -20, lean: 16, px: 11, bob: 3 });
  const keys = twin
    ? [K(0), K(0.24, { fa1: 40, fa2: 120, wpn: 150, ba1: 30, ba2: 110, bw: 160, lean: -4, px: -4 }), K(0.36, k1), K(0.5, { fa1: 40, fa2: 110, wpn: 150, ba1: 30, ba2: 110, bw: 160, lean: 2 }), K(0.62, L({ fa1: 30, fa2: 80, ba1: 92, ba2: 90, bw: 90, lean: 16, px: 11, bob: 3 })), K(1)]
    : [K(0), K(0.3, { fa1: 40, fa2: 120, wpn: 150, lean: -4, px: -4 }), K(0.44, k1), K(0.68, Object.assign({}, k1, { px: 10 })), K(1)];
  return {
    dur: 0.52, st, // 原本是劍的揮砍（0.52）
    keys,
    events: twin ? [{ t: 0.34, fx: 'thrust' }, { t: 0.38, fx: 'bolt', shape: 'qi' }, { t: 0.6, fx: 'thrust' }] : [{ t: 0.4, fx: 'thrust' }, { t: 0.42, fx: 'bolt', shape: 'qi' }],
  };
}
function rangedAttack(st, h) {
  switch (SG.rangedStyle && SG.rangedStyle(h)) {
    case 'bow': return shot(st);
    case 'xbow': return xbowShot(st);
    case 'fan': return fanSwing(st);
    case 'staff': return staffCast(st);
    case 'brush': return brushStroke(st);
    case 'card': return throwArm(st, 'card', false);
    case 'knife': return throwArm(st, 'knife', false);
    case 'dart': return throwArm(st, 'dart', true);
    case 'qi': return qiThrust(st, false);
    case 'qi2': return qiThrust(st, true);
  }
  return null;
}

// ---------- 技能原型 ----------
// 176 個技能共用這些原型；每個技能只提供名稱、顏色與少數參數。
const RAISE = { lean: -8, head: -12, fa1: 172, fa2: 178, wpn: 182, ba1: -24, ba2: 12, fl1: 14, bl1: -16 };
const POINT = { lean: 14, fa1: 98, fa2: 96, wpn: 94, ba1: -40, ba2: -10, fl1: 30, fl2: 18, bl1: -28, bl2: -38, px: 6, bob: 2 };
const WIND = { lean: -6, fa1: 160, fa2: 195, wpn: 230, px: -2, ba1: -30 };
const DOWN = L({ lean: 22, fa1: 78, fa2: 95, wpn: 65, px: 10, bob: 3, ba1: -40, ba2: -20 });
const hold = (p, o) => Object.assign({}, p, o);

const ARCH = {
  crescent: () => ({ dur: 1.1, keys: [K(0), K(0.26, { lean: -12, head: -6, fa1: 175, fa2: 200, wpn: 250, ba1: -30, bob: 5, fl1: 26, fl2: 4, bl1: -26, bl2: -30 }),
    K(0.42, { lean: 30, fa1: 70, fa2: 90, wpn: 58, ba1: -45, ba2: -25, px: 12, bob: 6, fl1: 42, fl2: 30, bl1: -42, bl2: -52 }),
    K(0.8, { lean: 26, fa1: 70, fa2: 88, wpn: 56, ba1: -45, ba2: -25, px: 12, bob: 6, fl1: 42, fl2: 30, bl1: -42, bl2: -52 }), K(1)],
    events: [{ t: 0.36, fx: 'swoosh', a1: 250, a2: 50, big: 1 }, { t: 0.4, fx: 'crescent' }] }),
  dash: () => {
    const T = (px) => L({ lean: 20, fa1: 88, fa2: 90, wpn: 92, ba1: 70, ba2: 90, bob: 3, px });
    const R = (px) => ({ lean: 6, fa1: 42, fa2: 66, wpn: 95, ba1: 40, ba2: 85, px, fl1: 24, fl2: 12, bl1: -24, bl2: -30 });
    return { dur: 1.25, keys: [K(0), K(0.08, R(0)), K(0.15, T(24)), K(0.22, R(32)), K(0.29, T(56)), K(0.36, R(64)), K(0.43, T(88)), K(0.5, R(96)),
      K(0.58, { lean: 10, fa1: 125, fa2: 140, wpn: 128, ba1: 90, ba2: 120, px: 118, py: -14, fl1: 30, fl2: 0, bl1: -30, bl2: -50 }),
      K(0.74, { lean: 8, fa1: 120, fa2: 135, wpn: 125, ba1: 90, ba2: 120, px: 118, py: 0, bob: 3 }),
      K(0.88, { lean: -6, px: 60, py: -20, fl1: 30, fl2: -10, bl1: -10, bl2: -40 }), K(1)],
    events: [{ t: 0.12, fx: 'ghost' }, { t: 0.14, fx: 'thrust' }, { t: 0.26, fx: 'ghost' }, { t: 0.28, fx: 'thrust' },
      { t: 0.4, fx: 'ghost' }, { t: 0.42, fx: 'thrust' }, { t: 0.54, fx: 'ghost' }, { t: 0.57, fx: 'swoosh', a1: 100, a2: 170 }] };
  },
  fire: (sk) => ({ dur: 1.35, keys: [K(0), K(0.26, RAISE), K(0.44, hold(RAISE, { lean: -9 })), K(0.56, POINT), K(0.9, hold(POINT, { lean: 12 })), K(1)],
    events: [{ t: 0.3, fx: 'glow' }, { t: 0.52, fx: 'swoosh', a1: 180, a2: 95 }].concat([95, 150, 205, 260].map((d, i) => ({ t: 0.56 + i * 0.06, fx: 'fire', d, pal: sk.pal }))) }),
  command: (sk) => ({ dur: 1.3, keys: [K(0), K(0.28, { lean: -6, head: -8, fa1: 168, fa2: 176, wpn: 180, ba1: -30, ba2: 20, bob: -1 }),
    K(0.82, { lean: -5, head: -6, fa1: 166, fa2: 175, wpn: 179, ba1: -28, ba2: 20 }), K(1)],
    events: [{ t: 0.3, fx: 'aura' }, { t: 0.32, fx: 'seal', ch: sk.glyph || '令' }] }),
  roar: (sk) => ({ dur: 1.15, keys: [K(0), K(0.28, { lean: -16, head: -22, fa1: 120, fa2: 150, wpn: 175, ba1: -50, ba2: -20, bob: 1, fl1: 18, bl1: -18 }),
    K(0.44, L({ lean: 22, head: 6, mouth: 1, fa1: 92, fa2: 100, wpn: 100, ba1: -60, ba2: -40, bob: 4, px: 6 })),
    K(0.84, L({ lean: 20, head: 4, mouth: 1, fa1: 90, fa2: 100, wpn: 102, ba1: -58, ba2: -40, bob: 4, px: 6 })), K(1)],
    events: [{ t: 0.44, fx: 'roar' }].concat(sk.shout ? [{ t: 0.46, fx: 'shout', str: sk.shout }] : []) }),
  slam: () => {
    const hitP = { py: 0, bob: 9, lean: 28, fa1: 70, fa2: 80, wpn: 45, ba1: 62, ba2: 72, bw: 40, fl1: 40, fl2: 28, bl1: -38, bl2: -50, px: 24 };
    return { dur: 1.15, keys: [K(0), K(0.22, { bob: 7, lean: 10, fa1: 165, fa2: 195, wpn: 230, ba1: 160, ba2: 190, bw: 225, fl1: 30, fl2: -5, bl1: -25, bl2: -50 }),
      K(0.42, { py: -46, lean: -5, fa1: 175, fa2: 200, wpn: 240, ba1: 170, ba2: 195, bw: 235, fl1: 20, fl2: -20, bl1: -10, bl2: -40, px: 14 }),
      K(0.55, hitP), K(0.85, hold(hitP, { lean: 26 })), K(1)],
    events: [{ t: 0.55, fx: 'slam' }] };
  },
  bolt: (sk, h) => {
    const bow = h.wclass === 'bow', n = sk.n || 1;
    const aim = bow ? { fa1: 90, fa2: 90, wpn: 182, ba1: 82, ba2: 40, lean: 0 } : { fa1: 150, fa2: 170, wpn: 205, ba1: -20, lean: -6 };
    const rel = bow ? { fa1: 92, fa2: 92, wpn: 182, ba1: 55, ba2: -5, lean: -2, px: -3 } : { fa1: 90, fa2: 92, wpn: 100, ba1: -40, lean: 12, px: 5 };
    const ev = [];
    for (let i = 0; i < n; i++) ev.push({ t: 0.42 + i * (0.3 / n), fx: 'bolt', shape: sk.shape || (bow ? 'arrow' : 'orb'), dy: n > 1 ? (i - (n - 1) / 2) * 12 : 0, big: sk.big });
    return { dur: 0.9, keys: [K(0), K(0.3, aim), K(0.4, aim), K(0.48, rel), K(0.82, rel), K(1)], events: ev };
  },
  volley: (sk, h) => {
    const upP = h.wclass === 'bow' ? { fa1: 150, fa2: 150, wpn: 215, ba1: 120, ba2: 95, lean: -12, head: -14 } : hold(RAISE, {});
    return { dur: 1.3, keys: [K(0), K(0.28, upP), K(0.5, upP), K(0.62, hold(upP, { lean: -6 })), K(1)],
      events: [{ t: 0.36, fx: 'bolt', shape: 'arrow', up: 1 }, { t: 0.5, fx: 'rain', d: sk.d || 170 }] };
  },
  spin: () => {
    const A = L({ lean: 12, fa1: 100, fa2: 100, wpn: 95, ba1: -60, bob: 3 });
    const B = L({ lean: -4, fa1: -70, fa2: -80, wpn: -85, ba1: 60, bob: 3 });
    return { dur: 1.1, keys: [K(0), K(0.14, A), K(0.28, B), K(0.42, A), K(0.56, B), K(0.7, A), K(0.85, A), K(1)],
      events: [{ t: 0.14, fx: 'whirl' }, { t: 0.2, fx: 'swoosh', a1: 95, a2: -60 }, { t: 0.34, fx: 'swoosh', a1: -60, a2: 95 }, { t: 0.48, fx: 'swoosh', a1: 95, a2: -60 }, { t: 0.62, fx: 'swoosh', a1: -60, a2: 95 }] };
  },
  buff: () => {
    const p = { fa1: 150, fa2: 185, wpn: 190, ba1: -30, ba2: 20, lean: -6, head: -8, bob: 3, fl1: 20, bl1: -20 };
    return { dur: 1, keys: [K(0), K(0.3, p), K(0.75, p), K(1)], events: [{ t: 0.3, fx: 'buff' }] };
  },
  heal: () => {
    const p = { fa1: 130, fa2: 160, wpn: 175, ba1: 60, ba2: 110, lean: -4, head: -4 };
    return { dur: 1.1, keys: [K(0), K(0.3, p), K(0.8, p), K(1)], events: [{ t: 0.35, fx: 'heal' }] };
  },
  blink: () => ({ dur: 1, keys: [K(0), K(0.2, { bob: 6, lean: 14, fl1: 26, bl1: -26 }), K(0.3, { bob: 6, lean: 14 }), K(0.36, { px: 150, bob: 2, lean: 10 }),
    K(0.78, { px: 150 }), K(0.9, { px: 80, py: -18 }), K(1)],
    events: [{ t: 0.3, fx: 'puff' }, { t: 0.31, fx: 'ghost' }, { t: 0.37, fx: 'puff' }] }),
  hook: (sk) => {
    const r = { fa1: 90, fa2: 92, wpn: 95, lean: 14, px: 4, ba1: -40 };
    return { dur: 1.1, keys: [K(0), K(0.25, { fa1: 140, fa2: 170, wpn: 210, lean: -6 }), K(0.4, r), K(0.85, r), K(1)], events: [{ t: 0.4, fx: 'chain', d: sk.d || 220 }] };
  },
  thunder: (sk) => ({ dur: 1.1, keys: [K(0), K(0.3, RAISE), K(0.5, { fa1: 110, fa2: 120, wpn: 130, lean: 6 }), K(0.85, { fa1: 108, fa2: 118, wpn: 128, lean: 5 }), K(1)],
    events: [{ t: 0.3, fx: 'glow' }, { t: 0.5, fx: 'thunder', d: sk.d || 170 }] }),
  shield: () => {
    const g = { lean: -4, bob: 3.5, head: -3, fl1: 22, fl2: 10, bl1: -24, bl2: -18, fa1: 62, fa2: 150, wpn: 176, ba1: 58, ba2: 140, bw: 210 };
    return { dur: 1.2, keys: [K(0), K(0.2, g), K(0.85, g), K(1)], events: [{ t: 0.2, fx: 'shield' }] };
  },
  charge: (sk, h) => ({ dur: 1.1, keys: [K(0), K(0.15, { lean: 25, bob: 5, fl1: 20, bl1: -40, wpn: (STANCE[h.wclass] || {}).wpn - 40 }),
    K(0.6, L({ px: 160, lean: 28, bob: 2, flow: 10, wpn: 100, fa1: 70 })), K(0.75, { px: 166, lean: 10 }), K(0.9, { px: 90, py: -14 }), K(1)],
    events: [0.22, 0.32, 0.42, 0.52].map((t) => ({ t, fx: 'dust' })).concat([{ t: 0.6, fx: 'impact', pal: sk.pal }]) }),
  trap: (sk) => {
    const c = { bob: 9, lean: 30, fa1: 60, fa2: 40, wpn: 120, ba1: 60, ba2: 50, fl1: 40, fl2: 5, bl1: -20, bl2: -60 };
    return { dur: 1, keys: [K(0), K(0.3, c), K(0.7, c), K(1)], events: [{ t: 0.4, fx: 'trap', shape: sk.shape || 'caltrops', d: sk.d || 70 }] };
  },
  spikes: (sk) => ({ dur: 1.1, keys: [K(0), K(0.25, WIND), K(0.4, hold(DOWN, { wpn: 40, fa1: 60, lean: 26 })), K(0.8, hold(DOWN, { wpn: 42, fa1: 60, lean: 24 })), K(1)],
    events: [{ t: 0.36, fx: 'swoosh', a1: 230, a2: 50 }, { t: 0.4, fx: 'spikes', pal: sk.pal }] }),
  multi: () => {
    const U = { lean: 4, fa1: 150, fa2: 170, wpn: 170, px: 22, ba1: -30 };
    const T = L({ lean: 18, fa1: 88, fa2: 90, wpn: 92, px: 38, bob: 3, ba1: -40 });
    return { dur: 1, keys: [K(0), K(0.15, WIND), K(0.27, hold(DOWN, { px: 12 })), K(0.45, U), K(0.62, T), K(0.85, T), K(1)],
      events: [{ t: 0.22, fx: 'swoosh', a1: 230, a2: 65 }, { t: 0.38, fx: 'swoosh', a1: 65, a2: 170 }, { t: 0.56, fx: 'thrust' }] };
  },
  summon: () => ({ dur: 1.3, keys: [K(0), K(0.28, { lean: -6, head: -8, fa1: 168, fa2: 176, wpn: 180, ba1: -30, ba2: 20 }), K(0.85, { lean: -5, fa1: 166, fa2: 175, wpn: 179, ba1: -28, ba2: 20 }), K(1)],
    events: [{ t: 0.3, fx: 'aura' }, { t: 0.38, fx: 'summon' }] }),
  passive: () => {
    const p = { bob: 2, lean: -2, head: -4 };
    return { dur: 0.9, keys: [K(0), K(0.3, p), K(0.7, p), K(1)], events: [{ t: 0.25, fx: 'buff', small: 1 }] };
  },
  stealth: () => {
    const c = { bob: 5, lean: 10, fl1: 22, bl1: -22 };
    return { dur: 1, keys: [K(0), K(0.25, c), K(0.6, c), K(1)], events: [{ t: 0.25, fx: 'puff' }, { t: 0.28, fx: 'fade' }] };
  },
};
// 技能從施放到命中的時間點（佔動作長度的比例），給遊戲邏輯與示範用
SG.HIT_AT = { crescent: 0.6, dash: 0.35, fire: 0.75, command: 0.45, roar: 0.5, slam: 0.55, bolt: 0.55, volley: 0.75, spin: 0.35, buff: 0.4, heal: 0.4,
  blink: 0.4, hook: 0.5, thunder: 0.55, shield: 0.3, charge: 0.6, trap: 0.5, spikes: 0.55, multi: 0.3, summon: 0.5, passive: 0.3, stealth: 0.3 };
SG.ARCH_NAMES = { crescent: '刀氣', dash: '連刺突進', fire: '地面火柱', command: '號令光環', roar: '怒吼', slam: '躍起重砸', bolt: '投射物', volley: '箭雨',
  spin: '旋風斬', buff: '自身強化', heal: '範圍治療', blink: '瞬移', hook: '鉤鎖', thunder: '落雷', shield: '護盾', charge: '衝撞', trap: '陷阱',
  spikes: '地裂', multi: '連斬', summon: '召喚', passive: '被動', stealth: '隱身' };

function skillAnim(st, h, sk) {
  const a = (ARCH[sk.arch] || ARCH.buff)(sk, h);
  const col = sk.col || null;
  a.st = st;
  a.events = [{ t: 0.02, fx: 'name', str: sk.passive ? sk.name + '（被動）' : sk.name, shake: sk.arch === 'roar' }]
    .concat(a.events || []).map((e) => Object.assign({ col }, e));
  return a;
}

SG.buildAnims = function (h) {
  if (Object.prototype.hasOwnProperty.call(h, '_anims')) return h._anims; // 形態的 spec 以本體為原型：要自己那份（弓和戟動作不同）
  const st = Object.assign({}, STANCE[h.wclass]);
  const atk = { spear: thrust, snake: thrust, bow: shot }[h.weapon];
  const A = {
    idle: { loop: true, dur: 1.6, proc: idle(st) },
    walk: { loop: true, dur: 0.8, proc: gait(st, false) },
    run: { loop: true, dur: 0.5, proc: gait(st, true) },
    defend: { loop: true, dur: 1.4, proc: guard(st, h) },
    attack: punchUp((h.ranged && rangedAttack(st, h)) || (atk ? atk(st) : h.weapon === 'fan' ? cast(st, 'wind') : h.weapon === 'staff' && h.ranged ? cast(st, 'orb') : slash(st, h.wclass === 'twin'))), // 遠程依兵器（v0.3.4）；近戰拿杖（凌統三節棍）照樣揮擊，不像施法
    hit: punchUp(hit(st)), jump: jump(st), fall: fall(st),
  };
  (h.skills || []).forEach((sk, i) => { A['s' + i] = punchUp(skillAnim(st, h, sk)); });
  A.skill = A.s3 || A.attack;
  h._anims = A;
  return A;
};
SG.poseOf = function (h, name, u) {
  const a = SG.buildAnims(h)[name];
  return a.proc ? a.proc(u) : sample(a.keys, u, a.st);
};

// ---------- 角色實體 ----------
class Actor {
  constructor(spec, x, y, f, scale, o) {
    o = o || {};
    this.spec = spec; this.x = x; this.y = y; this.f = f; this.s = scale * (spec.size || 1);
    this.anims = SG.buildAnims(spec);
    this.base = 'idle'; this.cur = 'idle'; this.t = Math.random() * 1.6; this.lastU = 0;
    this.alpha = 1; this.hp = 1; this.vx = 0; this.lock = false;
    this.ring = o.ring || null; this.dash = !!o.dash; this.team = o.team || null;
    this.onEnd = null; this.P = null; this.stage = null;
  }
  // 換一份武將設定（形態切換：兵器、動作都換）；正在做的動作新設定沒有就回到待機
  setSpec(spec) {
    this.spec = spec; this.anims = SG.buildAnims(spec);
    if (!this.anims[this.cur]) { this.cur = 'idle'; this.t = 0; }
    if (!this.anims[this.base]) this.base = 'idle';
  }
  play(name, onEnd) {
    const a = this.anims[name];
    if (!a) return;
    if (a.loop) this.base = name;
    this.cur = name; this.t = 0; this.lastU = 0; this.onEnd = onEnd || null;
  }
  busy() { return !this.anims[this.cur].loop; }
  poseAt(a, u) { return a.proc ? a.proc(u) : sample(a.keys, u, a.st); }
  pose() {
    const a = this.anims[this.cur];
    const u = a.loop ? (this.t / a.dur) % 1 : Math.min(1, this.t / a.dur);
    return this.poseAt(a, u);
  }
  update(dt) {
    this.x += this.vx * dt;
    const a = this.anims[this.cur];
    this.t += dt;
    if (a.loop) return;
    const u = Math.min(1, this.t / a.dur);
    if (a.events && this.stage) for (const ev of a.events) if (ev.t > this.lastU && ev.t <= u) { const P = this.poseAt(a, ev.t); if (this.noPx) P.px = 0; SG.spawn(this.stage, this, ev, P); }
    this.lastU = u;
    if (this.t >= a.dur) {
      const cb = this.onEnd; this.onEnd = null;
      if (!a.hold) { this.cur = this.base; this.t = 0; this.lastU = 0; }
      if (cb) cb(this);
    }
  }
  // 次要動作：一個跟著前進速度走的彈簧（阻尼不足，停下來會回盪），驅動披風、頭髮、飄帶
  spring(time) {
    const dt = this.lt ? Math.min(0.05, Math.max(0, (time - this.lt) / 1000)) : 0;
    this.lt = time;
    if (!dt) { this.lx = this.x; return this.sd || 0; }
    const fwd = ((this.x - (this.lx == null ? this.x : this.lx)) / dt) * this.f; this.lx = this.x;
    const target = Math.max(-6, Math.min(16, fwd / 22));
    this.sv = (this.sv || 0) + (140 * (target - (this.sd || 0)) - 9 * (this.sv || 0)) * dt;
    this.sd = (this.sd || 0) + this.sv * dt;
    return this.sd;
  }
  draw(ctx, time) {
    const P = this.pose(); if (this.noPx) P.px = 0; this.P = P;
    if (this.spec.legend && SG.legendPose) SG.legendPose(this, P, time); // 傳說級造型的專屬姿勢（殭屍跳）：只動畫面，位置不變
    P.drag = this.spring(time); P.flow += Math.max(0, P.drag) * 0.6;
    // 站著發呆時偶爾東張西望、換一下拿兵器的姿勢（每個人錯開，不會整排一起動）
    if (this.cur === 'idle') {
      if (this.seed == null) this.seed = Math.random();
      const ph = (time / 6500 + this.seed) % 1;
      if (ph < 0.18) { const q = Math.sin((ph / 0.18) * Math.PI); P.head += (this.seed < 0.5 ? -9 : 7) * q; P.wpn += 8 * q; P.lean -= 2 * q; }
    }
    const b = this.spec.bulk || 1, air = Math.min(1, Math.max(0, -P.py) / 60), sx = this.f * P.px;
    ctx.save(); ctx.translate(this.x, this.y); ctx.scale(this.s, this.s); ctx.globalAlpha = this.alpha;
    castShadow(ctx, sx, 0, 22 * b * (1 - air * 0.4), 6 * (1 - air * 0.4), 100 * (1 - air), 0.22 - air * 0.1);
    if (this.ring) {
      ctx.save(); ctx.strokeStyle = this.ring; ctx.lineWidth = 2.2;
      if (this.dash) ctx.setLineDash([6, 4]);
      ctx.beginPath(); ctx.ellipse(sx, 0, 27 * b, 8, 0, 0, TAU); ctx.stroke(); ctx.restore();
    }
    ctx.scale(this.f, 1);
    SG.setFace(this.f); drawHero(ctx, this.spec, P, time);
    // 受擊閃白：用疊加模式再畫一次，整個人亮一下
    if (this.flash > 0) { ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = this.alpha * Math.min(1, this.flash / 0.1) * 0.7; drawHero(ctx, this.spec, P, time); ctx.globalCompositeOperation = 'source-over'; }
    SG.setFace(1);
    ctx.restore();
  }
}
SG.Actor = Actor;
